import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Radio, Video, ExternalLink, Users, Navigation, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpotterIcon = ({ type }) => {
  const colors = {
    storm_chaser: "#ff0080",
    spotter: "#00f2ea",
    media: "#ccff00",
    researcher: "#7000ff"
  };
  
  return (
    <div 
      className="w-3 h-3 rounded-full animate-pulse"
      style={{ backgroundColor: colors[type] || "#fff" }}
    />
  );
};

export default function StormChasers({ lat, lon }) {
  const [spotters, setSpotters] = useState([]);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("spotters"); // spotters, streams
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [spottersRes, streamsRes] = await Promise.all([
        axios.get(`${API}/weather/spotters`, { params: { lat, lon, radius: 200 } }),
        axios.get(`${API}/weather/streams`)
      ]);
      setSpotters(spottersRes.data.spotters || []);
      setStreams(streamsRes.data.streams || []);
    } catch (error) {
      console.error("Failed to fetch storm chaser data:", error);
    } finally {
      setLoading(false);
    }
  }, [lat, lon]);
  
  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);
  
  const activeSpotters = spotters.filter((spotter) => /active|reporting|en_route/i.test(spotter.status || '')).length;
  const chaserCount = spotters.filter((spotter) => spotter.type === 'storm_chaser').length;
  const mediaCount = spotters.filter((spotter) => spotter.type === 'media').length;
  const reportingCount = spotters.filter((spotter) => /reporting/i.test(spotter.status || '')).length;
  const northboundCount = spotters.filter((spotter) => Number(spotter.heading) <= 45 || Number(spotter.heading) >= 315).length;
  const eastboundCount = spotters.filter((spotter) => Number(spotter.heading) > 45 && Number(spotter.heading) <= 135).length;
  const southboundCount = spotters.filter((spotter) => Number(spotter.heading) > 135 && Number(spotter.heading) <= 225).length;
  const westboundCount = spotters.filter((spotter) => Number(spotter.heading) > 225 && Number(spotter.heading) < 315).length;
  
  return (
    <div className="space-y-4" data-testid="storm-chasers-widget">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="storm-summary-grid">
        <div className="glass-light rounded-xl p-2.5" data-testid="storm-active-spotters-summary">
          <p className="text-[11px] text-white/45">Active</p>
          <p className="text-lg font-bold text-[#00f2ea]">{activeSpotters}</p>
        </div>
        <div className="glass-light rounded-xl p-2.5" data-testid="storm-live-streams-summary">
          <p className="text-[11px] text-white/45">Streams</p>
          <p className="text-lg font-bold text-[#ff0080]">{streams.length}</p>
        </div>
        <div className="glass-light rounded-xl p-2.5" data-testid="storm-chasers-summary">
          <p className="text-[11px] text-white/45">Chasers</p>
          <p className="text-lg font-bold">{chaserCount}</p>
        </div>
        <div className="glass-light rounded-xl p-2.5" data-testid="storm-reporting-summary">
          <p className="text-[11px] text-white/45">Reporting</p>
          <p className="text-lg font-bold">{reportingCount}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab("spotters")}
          className={`rounded-full ${
            activeTab === "spotters" 
              ? "bg-[#00f2ea]/20 text-[#00f2ea]" 
              : "text-white/60"
          }`}
          data-testid="storm-chasers-spotters-tab-button"
        >
          <Users className="w-4 h-4 mr-1" />
          Spotters ({spotters.length})
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setActiveTab("streams")}
          className={`rounded-full ${
            activeTab === "streams" 
              ? "bg-[#ff0080]/20 text-[#ff0080]" 
              : "text-white/60"
          }`}
          data-testid="storm-chasers-streams-tab-button"
        >
          <Video className="w-4 h-4 mr-1" />
          Live Streams
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={fetchData}
          disabled={loading}
          className="ml-auto rounded-full"
          data-testid="storm-chasers-refresh-button"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>
      
      {/* Spotters Tab */}
      {activeTab === "spotters" && (
        <div className="space-y-2">
        <div className="space-y-1.5 max-h-[760px] overflow-y-auto pr-1" data-testid="storm-spotters-list">
          {spotters.length === 0 ? (
            <p className="text-sm text-white/40 text-center py-4" data-testid="storm-empty-spotters-message">
              No active spotters in this area
            </p>
          ) : (
            spotters.map((spotter, idx) => (
              <motion.div
                key={spotter.id}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.03 }}
                className="flex items-center gap-2 px-2 py-1.5 glass-light rounded-xl min-w-0"
                data-testid={`storm-spotter-${spotter.id}`}
              >
                <div className="scale-75 -mx-1"><SpotterIcon type={spotter.type} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{spotter.name}</p>
                  <p className="text-[10px] text-white/40 capitalize leading-tight truncate">
                    {spotter.type.replace("_", " ")} • {spotter.status}
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-white/40 shrink-0">
                  <Navigation 
                    className="w-3 h-3" 
                    style={{ transform: `rotate(${spotter.heading}deg)` }}
                  />
                  <span>{spotter.heading}°</span>
                </div>
              </motion.div>
            ))
          )}
          
          {/* Legend */}
          <div className="flex items-center justify-center gap-4 pt-0.5 text-[10px] text-white/40" data-testid="storm-spotter-legend">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-[#ff0080]" />
              <span>Chaser</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-[#00f2ea]" />
              <span>Spotter</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-[#ccff00]" />
              <span>Media</span>
            </div>
          </div>
        </div>

        {spotters.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="storm-spotter-detail-grid">
            <div className="glass-light rounded-xl p-2.5" data-testid="storm-movement-summary">
              <h4 className="text-xs font-semibold mb-2">Movement read</h4>
              <div className="grid grid-cols-4 gap-1 text-center text-xs text-white/60">
                <div><p className="font-bold text-white">{northboundCount}</p><p>N</p></div>
                <div><p className="font-bold text-white">{eastboundCount}</p><p>E</p></div>
                <div><p className="font-bold text-white">{southboundCount}</p><p>S</p></div>
                <div><p className="font-bold text-white">{westboundCount}</p><p>W</p></div>
              </div>
            </div>
            <div className="glass-light rounded-xl p-2.5" data-testid="storm-source-mix-summary">
              <h4 className="text-xs font-semibold mb-2">Source mix</h4>
              <div className="space-y-1 text-xs text-white/60">
                <div className="flex justify-between"><span>Storm chasers</span><span>{chaserCount}</span></div>
                <div className="flex justify-between"><span>Media units</span><span>{mediaCount}</span></div>
                <div className="flex justify-between"><span>Local spotters</span><span>{Math.max(0, spotters.length - chaserCount - mediaCount)}</span></div>
              </div>
            </div>
          </div>
        )}
        </div>
      )}
      
      {/* Streams Tab */}
      {activeTab === "streams" && (
        <div className="space-y-3" data-testid="storm-streams-panel">
          <div className="grid grid-cols-2 gap-2" data-testid="storm-stream-summary-grid">
            <div className="glass-light rounded-xl p-3">
              <p className="text-[11px] text-white/45">Available</p>
              <p className="text-lg font-bold text-[#ff0080]">{streams.length}</p>
            </div>
            <div className="glass-light rounded-xl p-3">
              <p className="text-[11px] text-white/45">24/7</p>
              <p className="text-lg font-bold">{streams.filter((stream) => stream.always_live).length}</p>
            </div>
          </div>
          {streams.length === 0 ? (
            <p className="text-sm text-white/40 text-center py-4" data-testid="storm-empty-streams-message">
              No streams available
            </p>
          ) : (
            <div className="space-y-2 max-h-[620px] overflow-y-auto pr-1" data-testid="storm-streams-list">
            {streams.map((stream, idx) => (
              <motion.a
                key={stream.id}
                href={stream.youtube_url || stream.channel_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`stream-${stream.id}`}
                aria-label={`Open ${stream.name} weather stream`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="flex items-center gap-3 p-3 glass-light rounded-xl hover:bg-white/10 transition-colors group min-w-0"
              >
                <div className="relative">
                  <Video className="w-8 h-8 text-[#ff0080]" />
                  {stream.always_live && (
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-medium truncate">{stream.name}</p>
                    {stream.always_live && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 border border-red-500/30">24/7</span>
                    )}
                    {stream.type === "storm_chaser" && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/50">Storm Chaser</span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 truncate">{stream.description}</p>
                </div>
                <div className="flex items-center gap-2 text-xs text-white/35 group-hover:text-white/80 transition-colors">
                  <span>{stream.always_live ? "Open live" : "Open channel"}</span>
                  <ExternalLink className="w-4 h-4" />
                </div>
              </motion.a>
            ))}
            </div>
          )}
          
          <p className="text-xs text-white/40 text-center pt-2">
            Storm chasers stream live during severe weather events
          </p>
        </div>
      )}
    </div>
  );
}
